import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import RichTextEditor from 'react-rte';
import { convertHtmlToString, convertMessageToHtmlAndTrimSpaces } from '../utils';
import i18nContext from 'components/i18n-context';
import { MAX_MESSAGE_LENGTH, MAX_TOPIC_SUBJECT_LENGTH, MAX_UPLOADED_FILES_SIZE } from 'components/common/constants';
import Loader from 'components/common/Loader';
import { convertBytesToMegabytes, getErrorMessageForAlert, messageRegExp, prepareFieldErrors } from 'services/utils';
import { AttachDoc } from 'uikit/AttachDoc/AttachDoc';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import '../Messages.scss';

const NewMessageForm = ({
  generateSecurityCode,
  newTopicData,
  setNewTopicData,
  isLoading,
  error,
  clearError,
  uploadedFiles,
  uploadDocuments,
  removeAttachment,
  isShowingInternalActionConfirmation
}) => {
  const i18n = useContext(i18nContext);
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [messageValue, setMessageValue] = useState(
    newTopicData?.message_body
      ? RichTextEditor.createValueFromString(newTopicData?.message_body, 'html')
      : RichTextEditor.createEmptyValue()
  );

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      subject: newTopicData?.subject ? newTopicData?.subject : '',
      message: newTopicData?.message_body ? newTopicData?.message_body : ''
    },
    validationSchema: Yup.object({
      subject: Yup.string()
        .required(i18n.getMessage('messages.error.subject.empty'))
        .trim()
        .min(1, i18n.getMessage('messages.error.subject.empty'))
        .max(
          MAX_TOPIC_SUBJECT_LENGTH,
          i18n.getMessage('messages.error.subject.maxSize', { size: MAX_TOPIC_SUBJECT_LENGTH })
        )
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat')),
      message: Yup.string()
        .required(i18n.getMessage('messages.error.message.empty'))
        .max(MAX_MESSAGE_LENGTH, i18n.getMessage('messages.error.message.maxSize', { size: MAX_MESSAGE_LENGTH }))
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat'))
    }),
    onSubmit: (values) => {
      setNewTopicData(values.subject.trim(), convertMessageToHtmlAndTrimSpaces(messageValue));
      generateSecurityCode();
    }
  });

  const { values, errors, handleSubmit, handleChange, validateField, setFieldValue, submitCount } = form;

  useEffect(() => {
    clearError();
    setNewTopicData('', '');
  }, []);

  useEffect(() => {
    setFieldValue('message', convertHtmlToString(messageValue));
  }, [messageValue, setFieldValue]);

  const onBlurSubjectField = () => {
    validateField('subject');
    if (serverFieldsErrors?.subject) {
      clearError();
    }
  };

  const onBlurMessageField = () => {
    validateField('message');
    if (serverFieldsErrors?.message) {
      clearError();
    }
  };

  return (
    <div className={'messages-right-side'}>
      <form action={'#'} id={'send'} onSubmit={handleSubmit} className='new-message-form'>
        <Input
          className={'new-message-form-subject-input'}
          type={'text'}
          autoFocus={true}
          label={i18n.getMessage('messages.form.label.subject')}
          name={'subject'}
          value={values.subject}
          isDisabled={isShowingInternalActionConfirmation}
          onChange={handleChange}
          onBlur={onBlurSubjectField}
          initialStatus={submitCount}
          error={errors?.subject || serverFieldsErrors?.subject}
          max={MAX_TOPIC_SUBJECT_LENGTH}
          /* eslint-disable-next-line max-len */
          symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
            amount: MAX_TOPIC_SUBJECT_LENGTH - values.subject.trim().length
          })}
        />
        <Input
          className={'chat-form-input'}
          type={'text-editor'}
          label={i18n.getMessage('messages.form.input.message.label')}
          name={'message'}
          isDisabled={isShowingInternalActionConfirmation}
          value={messageValue}
          onChange={setMessageValue}
          onBlur={onBlurMessageField}
          initialStatus={submitCount}
          max={MAX_MESSAGE_LENGTH}
          rows={4}
          error={errors?.message || serverFieldsErrors?.message}
          /* eslint-disable-next-line max-len */
          symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
            amount: MAX_MESSAGE_LENGTH - values.message.length
          })}
        />
        <AttachDoc
          label={i18n.getMessage('messages.form.attachDoc.label', { amount: uploadedFiles.length })}
          className={'new-message-form-attachments'}
          type={'file'}
          size={MAX_UPLOADED_FILES_SIZE}
          isDisabled={isShowingInternalActionConfirmation}
          files={uploadedFiles}
          onChange={uploadDocuments}
          onRemoveFiles={removeAttachment}
          error={error?.type === 'attachDoc' && getErrorMessageForAlert(i18n, error)}
          hintText={i18n.getMessage('attachDoc.hint.text', {
            maxFilesSize: convertBytesToMegabytes(MAX_UPLOADED_FILES_SIZE)
          })}
        />

        <Button
          className={'new-message-form-button'}
          type={'primary'}
          roleType={'submit'}
          size={'large'}
          fullWidth={true}
          onClick={() => {}}
          isDisabled={isLoading}
        >
          {isLoading ? <Loader /> : i18n.getMessage('messages.button.sendMessage')}
        </Button>
      </form>
    </div>
  );
};

NewMessageForm.propTypes = {
  isShowingInternalActionConfirmation: PropTypes.bool.isRequired,
  generateSecurityCode: PropTypes.func.isRequired,
  newTopicData: PropTypes.object.isRequired,
  setNewTopicData: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  clearError: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired
};

export default NewMessageForm;
